// Utility functions for calculating image loading priority based on viewport and position

export type ImagePriority = 'high' | 'medium' | 'low';

export interface PriorityConfig {
  index: number;
  isSwiper?: boolean;
  isAboveFold?: boolean;
  isHero?: boolean;
  windowWidth?: number;
}

// Calculate swiper token count based on viewport
export const getSwiperTokenCount = (windowWidth: number): number => {
  if (windowWidth >= 1440) return 8; // Large desktop: 7.5 slides
  if (windowWidth >= 1200) return 7; // Desktop: 6.5 slides
  if (windowWidth >= 1120) return 6; // Small desktop: 5.5 slides
  if (windowWidth >= 980) return 5; // Tablet: 4.5 slides
  if (windowWidth >= 768) return 4; // Mobile: 3.5 slides
  return 3; // Small mobile: 2.5 slides
};

// Get critical preload count for swiper (only first 3 images)
export const getSwiperPreloadCount = (): number => {
  return 3; // Always preload only first 3 swiper images
};

// Calculate table priority token count based on viewport
export const getTablePriorityCount = (windowWidth: number): number => {
  if (windowWidth >= 980) return 12; // Desktop: ~20 tokens visible
  if (windowWidth >= 768) return 6; // Tablet: ~10 tokens visible
  return 4; // Mobile: ~7 tokens visible
};

// Calculate total priority count for preloading
export const getTotalPriorityCount = (windowWidth: number): number => {
  const swiperCount = getSwiperTokenCount(windowWidth);
  const tableCount = getTablePriorityCount(windowWidth);
  return swiperCount + tableCount;
};

/**
 * Calculate image loading priority based on position and context
 */
export function getImagePriority(config: PriorityConfig): ImagePriority {
  const { index, isSwiper = false, isAboveFold = false, isHero = false} = config;

  // Hero images always get high priorityπ 
  if (isHero) {
    return 'high';
  }

  // Above-fold images get high priority
  if (isAboveFold) {
    return 'high';
  }

  // Swiper images - only preload first 3 for optimal performance
  if (isSwiper) {
    if (index < 3) return 'high';
    return 'low';
  }

  // Table/list images - more conservative priority
  if (index < 8) return 'high';
  if (index < 20) return 'medium';
  return 'low';
}

/**
 * Get root margin for intersection observer based on priority
 */
export function getRootMargin(priority: ImagePriority): string {
  switch (priority) {
    case 'high':
      return '50px'; // Load when within 50px
    case 'medium':
      return '100px'; // Load when within 100px
    case 'low':
      return '200px'; // Load when within 200px
    default:
      return '100px';
  }
}

/**
 * Check if an image should be above the fold based on index and screen size
 */
export function isAboveFold(index: number, windowWidth: number = 1024): boolean {
  // More realistic above-fold calculation based on typical viewport heights
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  
  if (isMobile) {
    // Mobile: ~8-10 tokens visible above fold
    return index < 10;
  } else if (isTablet) {
    // Tablet: ~12-15 tokens visible above fold
    return index < 15;
  } else {
    // Desktop: ~15-18 tokens visible above fold
    return index < 18;
  }
}

/**
 * Get loading strategy based on priority
 */
export function getLoadingStrategy(priority: ImagePriority): 'eager' | 'lazy' {
  return priority === 'high' ? 'eager' : 'lazy';
}

/**
 * Get preload strategy based on priority
 */
export function getPreloadStrategy(priority: ImagePriority): boolean {
  return priority === 'high';
}

// Check if image should be preloaded based on position and viewport
export const shouldPreloadImage = (
  index: number, 
  windowWidth: number, 
  isSwiper: boolean = false
): boolean => {
  const priority = getImagePriority({ index, isSwiper });
  return priority === 'high';
};

// Swiper-specific preload check (only first 3 images)
export const shouldPreloadSwiperImage = (index: number): boolean => {
  return index < 3;
};

// Get critical preload count for preload links (most important images)
export const getCriticalPreloadCount = (windowWidth: number, isSwiper: boolean = false): number => {
  // For swiper, always preload only 3 images
  if (isSwiper) {
    return 3;
  }
  
  // For other components, use responsive logic
  if (windowWidth >= 980) return 3; // Desktop: 3 most critical
  return 2; // Mobile/Tablet: 2 most critical
}; 